/** Shared shape for a property-address update propagated to SR and WO list rows. */
export type PropertyAddressData = {
  property_id: string;
  address: {
    street_address: string;
    zip_code: string;
    city: string;
    apt_suite_unit: string;
    state_code: string;
  };
  state: {
    value: string;
    label: string;
  };
  /** Legacy address values for the edit form (on the save responses and the broadcast). */
  editable_address?: PropertyAddressData['address'];
  /** True while the shown address comes from a building Google has verified. */
  address_verified?: boolean;
};

/**
 * Pull a valid property-address payload out of a Pusher `property.address.updated`
 * broadcast, or return null when the payload is incomplete. Shared by the SR and
 * WO list Pusher hooks so the guard lives in one place.
 */
export const extractPropertyAddressBroadcast = (
  response:
    | {
        property_id?: string;
        address?: PropertyAddressData['address'];
        state?: PropertyAddressData['state'];
        editable_address?: PropertyAddressData['editable_address'];
        address_verified?: boolean;
      }
    | null
    | undefined,
): PropertyAddressData | null => {
  if (response?.property_id && response?.address && response?.state) {
    return {
      property_id: response.property_id,
      address: response.address,
      state: response.state,
      // Open edit forms pre-fill from these, so they must follow the saved address.
      ...(response.editable_address ? { editable_address: response.editable_address } : {}),
      ...(response.address_verified !== undefined
        ? { address_verified: response.address_verified }
        : {}),
    };
  }

  return null;
};

type PropertyRow = {
  property?: {
    property_id?: string;
    address?: unknown;
    state?: unknown;
  } | null;
};

/**
 * Apply a property-address update to every cached row whose property matches
 * `data.property_id`. `originatingId`, when supplied (the PATCH-success path),
 * also forces that row to update even if its property_id is not yet populated;
 * the property-scoped broadcast omits it and relies purely on the match above.
 * Shared by the SR and WO list reducers.
 */
export const applyPropertyAddressToSource = <T extends PropertyRow>(
  source: Record<string, T> | undefined,
  data: PropertyAddressData,
  originatingId?: string,
): Record<string, T> => {
  const next: Record<string, T> = { ...(source ?? {}) };

  const applyAddress = (entry: T | undefined): T =>
    ({
      ...(entry ?? {}),
      property: {
        ...(entry?.property ?? {}),
        state: data.state,
        address: data.address,
        property_id: data.property_id,
        // Older payloads may omit these, so keep the row's values unless the update has them.
        ...(data.editable_address ? { editable_address: data.editable_address } : {}),
        ...(data.address_verified !== undefined ? { address_verified: data.address_verified } : {}),
      },
    }) as T;

  for (const key of Object.keys(next)) {
    if (next[key]?.property?.property_id === data.property_id) {
      next[key] = applyAddress(next[key]);
    }
  }

  if (originatingId) {
    next[originatingId] = applyAddress(next[originatingId]);
  }

  return next;
};
